// Only an upward user gesture can fetch history. A short transcript may not
// scroll at all, so wheel/touch/key intent also checks the top threshold.
export function watchUpwardHistoryScroll(el: HTMLElement, load: () => void): () => void {
  let previousTop = el.scrollTop
  let touchY: number | null = null
  let upwardUntil = 0
  const nearTop = () => { if (el.scrollTop <= 240) load() }
  const upward = () => {
    upwardUntil = Date.now() + 1500
    nearTop()
  }
  const wheel = (event: WheelEvent) => {
    if (event.deltaY < 0) upward()
    else upwardUntil = 0
  }
  const touchStart = (event: TouchEvent) => {
    touchY = event.touches[0]?.clientY ?? null
    upwardUntil = 0
  }
  const touchMove = (event: TouchEvent) => {
    const nextY = event.touches[0]?.clientY ?? null
    if (touchY !== null && nextY !== null) {
      if (nextY - touchY > 6) {
        upward()
        touchY = nextY
      } else if (touchY - nextY > 6) {
        upwardUntil = 0
        touchY = nextY
      }
    }
  }
  const keyDown = (event: KeyboardEvent) => {
    if (['ArrowUp', 'PageUp', 'Home'].includes(event.key)) upward()
    else upwardUntil = 0
  }
  const scroll = () => {
    const nextTop = el.scrollTop
    if (nextTop < previousTop && Date.now() <= upwardUntil) nearTop()
    previousTop = nextTop
  }
  el.addEventListener('wheel', wheel, { passive: true })
  el.addEventListener('touchstart', touchStart, { passive: true })
  el.addEventListener('touchmove', touchMove, { passive: true })
  el.addEventListener('keydown', keyDown)
  el.addEventListener('scroll', scroll, { passive: true })
  return () => {
    el.removeEventListener('wheel', wheel)
    el.removeEventListener('touchstart', touchStart)
    el.removeEventListener('touchmove', touchMove)
    el.removeEventListener('keydown', keyDown)
    el.removeEventListener('scroll', scroll)
  }
}

export const HISTORY_FIRST_ITEM_INDEX = 1_000_000

export function historyAfterReset<T extends { ts: number }>(
  messages: T[], resetAt: number, includeEarlier: boolean,
): T[] {
  return resetAt > 0 && !includeEarlier
    ? messages.filter((message) => message.ts > resetAt)
    : messages
}

// Anchor on the first message, not its day separator: prepending messages on
// that same day moves the separator, while the message retains its identity.
export function prependFirstItemIndex(
  previousIndex: number,
  previousMessageIndex: number,
  nextMessageIndex: number,
): number {
  return nextMessageIndex > previousMessageIndex && previousMessageIndex >= 0
    ? previousIndex - (nextMessageIndex - previousMessageIndex)
    : previousIndex
}
